import React from 'react'

import {
  Tooltip as MuiTooltip,
  type TooltipProps as MuiTooltipProps,
} from '@mui/material'

/**
 * The house tooltip.
 *
 * WHY THIS EXISTS AT ALL. `@repo/ui` previously shipped a 0-BYTE `Tooltip` stub — a name
 * in the barrel that resolved in TypeScript and exported nothing at runtime, so importing
 * it blanked the app. Call sites worked around it by importing MUI's directly, each
 * re-deciding placement, arrow and delay, and several left an explanatory comment about
 * the stub. The stub was later deleted, which removed the crash but left the shared
 * library with no tooltip at all and every surface on its own.
 *
 * This is the fix: one real implementation here, so a caller writes `Tooltip` from
 * `@repo/ui` like any other component and gets consistent behaviour. Do NOT reach past
 * this into `@mui/material` — if something is missing, add the prop here.
 *
 * DEFAULTS, and why they are these:
 *  - `arrow` on. A tooltip without one leaves the reader to work out which of several
 *    adjacent controls it belongs to, which is precisely when tooltips are used.
 *  - `enterDelay` 200ms. Long enough that sweeping the cursor across a toolbar does not
 *    fire a row of tooltips, short enough to feel immediate when you stop on one.
 *  - `enterNextDelay` matches, so moving BETWEEN adjacent targets does not flash.
 *  - `placement="top"`. Keeps the tooltip clear of the pointer and of anything below,
 *    which on a dense table is usually the next row.
 *  - ALIGNMENT follows the anchor near a viewport edge — see the modifier below.
 *
 * WRAPPING A DISABLED CONTROL: a disabled element fires no pointer events, so its
 * tooltip never opens. Wrap it in a `<span>` (the span receives the events), or use the
 * shared `Button`'s `disabledReason`, which handles this and the accessibility of it.
 */

/**
 * ─── Edge-aware alignment ───────────────────────────────────────────────────────────
 *
 * THE DEFECT. MUI's placement is centred on the anchor. When a centred tooltip would
 * overflow the viewport, Popper's `preventOverflow` SHIFTS it back inboard — so it lands
 * aligned to nothing: not centred on the anchor, not flush with any edge. The position is
 * residual rather than chosen, which reads as detached. Most visible on the Ask Vera
 * launcher, which sits hard against the right edge of the header.
 *
 * THE FIX. Where the centred tooltip would overflow, align it to the anchor on that side
 * instead: `-end` against the right/bottom edge, `-start` against the left/top edge. The
 * arrow still points at the anchor's centre (Popper's `arrow` modifier centres on the
 * reference, clamped inside the popper), so the tooltip reads as belonging to the control
 * AND has a chosen alignment.
 *
 * WHY A POPPER MODIFIER RATHER THAN MEASURING THE ANCHOR. "Would this overflow?" is not
 * answerable from the anchor alone — it depends on the tooltip's own width, which is not
 * known until it has rendered. Measuring the anchor and realigning past some distance
 * from the edge needs a threshold, and no threshold is correct: a 40px anchor 100px from
 * the right edge overflows under a 300px tooltip and does not under a 20px one. A modifier
 * runs INSIDE the positioning pipeline, where `state.rects.popper` is the measured tooltip
 * and the reference rect is live, so the question is answered rather than estimated.
 *
 * THE TRIGGER IS NOT A MAGIC NUMBER. It is exactly Popper's own overflow test with its own
 * default padding of 0: we realign precisely when, and only when, `preventOverflow` would
 * otherwise have shifted the centred tooltip. So there is no band of cases where one of
 * the two fires and the other does not, and nothing to tune.
 *
 * WHY `afterMain`, AND WHY IT MARKS `flip` DONE. `flip` chooses the BASE placement (above
 * vs below); we choose only the VARIATION, so we must run after it has had its say — hence
 * `afterMain`, which Popper runs strictly after every `main` modifier. Changing placement
 * requires `state.reset`, which restarts the whole cycle so `popperOffsets`/`offset`/
 * `arrow` are recomputed for the new placement. On that second pass `flip` re-derives from
 * `state.options.placement` — the ORIGINAL `top`, not our `top-end` — and, finding that
 * bare `top` "fits" by its fallback checks, would set the placement straight back to `top`
 * and discard our variation. So we set its `_skip`, the same flag `flip` sets on itself
 * once it has decided. That is not a way around `flip`: it has already answered the
 * question it is good at (is there room above?), and its only remedy for the sideways
 * overflow is to flip the base placement or the variation to the opposite one, which lands
 * back on centred. If a future Popper renames `_skip`, the symptom is that we revert to
 * centred — i.e. today's behaviour. It fails safe.
 *
 * DIRECTION. Popper's `-start`/`-end` are raw geometry, not writing-direction (which is
 * why MUI swaps them itself for RTL before creating the Popper). We pick the variation
 * from measured rects, so the choice is correct either way round without special-casing.
 *
 * LIMIT, stated rather than implied: the boundary tested is the visual viewport, not an
 * arbitrary clipping scroll container. `detectOverflow` would handle the general case, but
 * it lives in `@popperjs/core`, which this package does not declare as a dependency.
 * A tooltip clipped by something other than the viewport keeps today's behaviour.
 */
const EDGE_AWARE_MODIFIER_NAME = 'veraEdgeAwareAlignment'

/**
 * The slice of Popper's modifier argument this reads. Typed structurally rather than
 * imported, for the dependency reason in the LIMIT note above.
 */
interface EdgeAwareModifierArgs {
  name: string
  state: {
    placement: string
    reset: boolean
    elements: {
      popper: { ownerDocument?: Document | null }
      reference: {
        getBoundingClientRect: () => {
          top: number
          right: number
          bottom: number
          left: number
        }
      }
    }
    rects: { popper: { width: number; height: number } }
    modifiersData: Record<string, { _skip?: boolean } | undefined>
  }
}

const edgeAwareAlignment = {
  name: EDGE_AWARE_MODIFIER_NAME,
  enabled: true,
  phase: 'afterMain' as const,
  // Documentary: the phase already guarantees we run after every `main` modifier. Popper
  // skips a `requiresIfExists` name it cannot find, so this is safe if `flip` is disabled.
  requiresIfExists: ['flip'],
  data: { _skip: false },
  fn: ({ state, name }: EdgeAwareModifierArgs) => {
    const own = state.modifiersData[name]
    if (own?._skip) return

    const [basePlacement, variation] = state.placement.split('-')
    // Alignment already chosen — by the caller's `placement`, or by `flip`. Leave it.
    if (variation) return

    const horizontal = basePlacement === 'top' || basePlacement === 'bottom'
    const doc = state.elements.popper.ownerDocument
    const root = doc?.documentElement
    if (!root) return

    const anchor = state.elements.reference.getBoundingClientRect()
    // `clientWidth`/`clientHeight` exclude the scrollbar, matching the viewport rect
    // Popper itself measures against.
    const viewportLength = horizontal ? root.clientWidth : root.clientHeight
    const popperLength = horizontal
      ? state.rects.popper.width
      : state.rects.popper.height
    const anchorCentre = horizontal
      ? (anchor.left + anchor.right) / 2
      : (anchor.top + anchor.bottom) / 2

    // Where a CENTRED tooltip's two edges would land.
    const overflowEnd = anchorCentre + popperLength / 2 - viewportLength
    const overflowStart = popperLength / 2 - anchorCentre

    let next: string | undefined
    if (overflowEnd > 0 && overflowStart <= 0) next = `${basePlacement}-end`
    else if (overflowStart > 0 && overflowEnd <= 0) next = `${basePlacement}-start`
    // Fits, or is wider than the viewport in both directions: no alignment helps, and
    // `preventOverflow` is the right tool for the second case. Leave it centred.
    if (!next) return

    if (own) own._skip = true
    const flipData = state.modifiersData.flip
    if (flipData) flipData._skip = true
    state.placement = next
    state.reset = true
  },
}

/**
 * Module-level so the array identity is stable across renders. `BasePopper` re-creates the
 * Popper instance whenever its `modifiers` prop changes identity, so a fresh array per
 * render would tear down and rebuild the positioner on every render.
 */
const EDGE_AWARE_MODIFIERS = [edgeAwareAlignment]

/** The common path — no caller `slotProps` — kept constant for the same reason. */
const EDGE_AWARE_SLOT_PROPS = { popper: { modifiers: EDGE_AWARE_MODIFIERS } }

export interface TooltipProps extends Omit<MuiTooltipProps, 'title'> {
  /** Tooltip content. Empty or undefined renders the child with no tooltip at all. */
  title?: React.ReactNode
}

export const Tooltip = ({
  title,
  arrow = true,
  placement = 'top',
  enterDelay = 200,
  enterNextDelay = 200,
  children,
  slotProps,
  ...rest
}: TooltipProps) => {
  // An empty title should not produce an invisible listener-bearing wrapper — MUI
  // renders one, and nesting that around another tooltip is how a hover handler gets
  // swallowed. Return the child untouched instead.
  if (!title) return children

  // A placement carrying a variation (`top-start`, `bottom-end`) has its alignment
  // specified; don't attach the modifier at all, so "explicit placement wins" is
  // structural rather than a condition the modifier has to remember. A BARE placement
  // (`top`, `right` — 58 of the 69 hand-set placements in the apps) specifies the side
  // only, and leaves the alignment to be filled in.
  //
  // Deliberately not a `useMemo`: the empty-title guard above returns early, so a hook
  // here would change hook order the moment a title appears.
  const alignmentIsExplicit = placement.includes('-')
  const callerModifiers = slotProps?.popper?.modifiers

  let resolvedSlotProps = slotProps
  if (!alignmentIsExplicit) {
    resolvedSlotProps = slotProps
      ? {
          ...slotProps,
          popper: {
            ...slotProps.popper,
            // Ours first: Popper merges modifiers by name, last wins, so a caller
            // supplying one of the same name still overrides us.
            modifiers: callerModifiers
              ? [...EDGE_AWARE_MODIFIERS, ...callerModifiers]
              : EDGE_AWARE_MODIFIERS,
          },
        }
      : EDGE_AWARE_SLOT_PROPS
  }

  return (
    <MuiTooltip
      title={title}
      arrow={arrow}
      placement={placement}
      enterDelay={enterDelay}
      enterNextDelay={enterNextDelay}
      slotProps={resolvedSlotProps}
      {...rest}
    >
      {children}
    </MuiTooltip>
  )
}
